/* ============================================================================
   Hemlock & Oak — custom.js
   ----------------------------------------------------------------------------
   Site-wide custom JavaScript, moved out of theme.liquid so it:
     - is cached by the browser (downloaded once, not re-shipped on every page)
     - survives theme updates (it is NOT inside the vendored theme.js bundle)
     - keeps theme.liquid readable

   Loaded from theme.liquid <head> with:
     <script src="{{ 'custom.js' | asset_url }}" defer></script>

   This is a CLASSIC (non-module) script on purpose. `defer` makes it
   non-blocking, while classic scope keeps the one deliberate global these
   snippets rely on (window.showVariantMediaInGallery, called from
   product-card markup) working exactly as it did inline. A module would seal
   that scope and break the reference.

   THE WHOLE FILE IS ONE IIFE — never add top-level const/let/class OUTSIDE it.
   WHY: in a classic script a top-level class/let/const becomes a GLOBAL
   lexical binding shared by every other classic script on the page.
   sections/cart-drawer.liquid still renders an inline copy of the cart-recs
   block, so `class CartRecsCarousel` already exists globally before this
   deferred file runs; when this file also declared it at top level the engine
   threw "Identifier 'CartRecsCarousel' has already been declared" BEFORE
   executing a single statement, silently killing EVERY block in this file.
   That is exactly how the Judge.me stars vanished on 2026-07-23: the stars'
   only surviving paint code lived here and the whole file was dead on
   arrival. Reordering blocks could never fix that (the abort happens
   pre-execution) — only removing all top-level lexical bindings does, hence
   the wrapper. Inside the IIFE every declaration is function-scoped, so no
   name collision with any other script is possible in either direction.

   Contents (the star restyle stays FIRST inside the IIFE so a runtime throw
   in a later block can never stop the stars from painting):
     1. Judge.me star restyle + Sezzle "or" text fix
     2. Product card interactions
     3. Swatch "show more" button
     4. Quick add-to-cart (.tm-add-bag) with animation
     5. Swatch change -> variant name label
     6. window.showVariantMediaInGallery (swatch hover -> gallery image)
     7. Cart recommendations carousel (inline twin in cart-drawer.liquid —
        guarded so both copies coexist; see the block note)
     8. Sold-out product card fade
     9. Tepo customizer widget mover
    10. Duo pair integrity (cart-wide orphan enforcement + load sweep)
    11. HEADSTART free gift (cart watcher + in-cart picker)
    12. Spec glossary popover (tappable spec terms on product cards)
   ========================================================================== */
(function () {

/* ----------------------------------------------------------------------------
   1. Judge.me star restyle + Sezzle "or" text fix

   replaceStars(): swaps Judge.me's star glyphs for your custom SVG.
   fixSezzle():    strips the leading "or " from the Sezzle button text.

   NOTE 1 (Sezzle): if you ever drop Sezzle, delete the fixSezzle() function and
     the `if (!sezzleDone) ...` line in the observer below — that's the whole
     Sezzle piece. Everything else here is Judge.me.

   NOTE 2 (perf): the observer scans the page on every DOM change and never
     disconnects, to catch stars that load asynchronously. It works, but it's
     the one block worth optimizing later. Kept faithful here on purpose.
   -------------------------------------------------------------------------- */
(function () {
  var STAR_PATH = 'M283.84 867.84 512 747.776l228.16 119.936a6.4 6.4 0 0 0 9.28-6.72l-43.52-254.08 184.512-179.904a6.4 6.4 0 0 0-3.52-10.88l-255.104-37.12L517.76 147.904a6.4 6.4 0 0 0-11.52 0L392.192 379.072l-255.104 37.12a6.4 6.4 0 0 0-3.52 10.88L318.08 606.976l-43.584 254.08a6.4 6.4 0 0 0 9.28 6.72z';
  var STAR_ON = "#ca8b38";
  var STAR_OFF = "#d9d9d9";

  /* Judge.me marks each star jdgm--on / jdgm--half / jdgm--off. A half star is
     drawn as the empty star with a bronze copy clipped to its left half —
     clip-path instead of an SVG gradient so no <defs> ids can collide when the
     same markup repeats per star. */
  function starSVG(state) {
    var paths;
    if (state === "half") {
      paths =
        '<path fill="' + STAR_OFF + '" d="' + STAR_PATH + '"/>' +
        '<path fill="' + STAR_ON + '" style="clip-path:inset(0 50% 0 0)" d="' + STAR_PATH + '"/>';
    } else {
      paths = '<path fill="' + (state === "on" ? STAR_ON : STAR_OFF) + '" d="' + STAR_PATH + '"/>';
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="16" height="16">' + paths + '</svg>';
  }

  function replaceStars() {
    var stars = document.querySelectorAll(".jdgm-star:not([data-star-replaced])");
    stars.forEach(function (star) {
      try {
      var state = "off";
      if (star.classList.contains("jdgm--on")) state = "on";
      else if (star.classList.contains("jdgm--half")) state = "half";
      star.innerHTML = starSVG(state);
      star.setAttribute("data-star-replaced", "true");
      star.style.setProperty("display", "inline-block", "important");
      star.style.setProperty("line-height", "0", "important");
      star.style.setProperty("font-size", "0", "important");
      star.style.setProperty("margin", "0", "important");
      star.style.setProperty("padding", "0", "important");
      } catch (e) { /* never let one node kill the pass */ }
    });
  }

  function fixSezzle() {
    var el = document.querySelector(".sezzle-button-text");
    if (el && el.firstChild && el.firstChild.nodeType === 3) {
      var txt = el.firstChild.textContent;
      if (txt.indexOf("or ") === 0) {
        el.firstChild.textContent = txt.replace(/^or /, "");
        return true;
      }
    }
    return false;
  }

  var sezzleDone = false;
  function runAll() {
    replaceStars();
    if (!sezzleDone) sezzleDone = fixSezzle();
  }
  var observer = new MutationObserver(runAll);
  function start() {
    runAll(); /* server HTML already carries jdgm--on/half classes — paint immediately */
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();


/* ----------------------------------------------------------------------------
   2. Product card interactions
   Shared handlers extracted from product-card and product-card-variant-display.
   -------------------------------------------------------------------------- */
document.addEventListener("change", function (e) {
  const radio = e.target;
  if (!(radio instanceof HTMLInputElement)) return;
  if (!radio.matches('.product-card__swatch-list [type="radio"], .product-card__variant-list [type="radio"]')) return;
  if (!radio.dataset.variantId) return;

  const card = radio.closest("product-card");
  if (!card) return;

  const hiddenInput = card.querySelector('.product-card__quick-buy form[is="product-form"] input[name="id"]');
  if (hiddenInput) {
    hiddenInput.value = radio.dataset.variantId;
    const purchase = hiddenInput.form.querySelector('[data-ho-action="add"], [data-ho-action="preorder"]');
    if (purchase && radio.hasAttribute('data-ho-preorder')) {
      const preorder = radio.dataset.hoPreorder === 'true';
      const available = radio.dataset.hoAvailable !== 'false';
      purchase.dataset.hoAction = available && preorder ? 'preorder' : 'add';
      purchase.disabled = !available;
      const label = purchase.querySelector('[data-ho-action-label]');
      if (label) label.textContent = !available ? purchase.dataset.hoSoldoutLabel : preorder ? purchase.dataset.hoPreorderLabel : purchase.dataset.hoAddLabel;
    }
  }
});

document.addEventListener("click", function (e) {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const showMoreBtn = target.closest(".show-more-button");
  if (!showMoreBtn) return;

  const swatchList = showMoreBtn.closest(".product-card__swatch-list");
  if (!swatchList) return;

  e.preventDefault();
  e.stopPropagation();

  swatchList.querySelectorAll(".hidden-swatch").forEach((swatch) => {
    swatch.style.display = "inline-flex";
  });
  showMoreBtn.remove();
}, true);

document.addEventListener("click", function (e) {
  const target = e.target;
  if (!(target instanceof Element)) return;

  const card = target.closest("variant-card");
  if (!card) return;
  if (target.closest("button") || target.closest("a") || target.closest("input")) return;

  const variantId = card.getAttribute("variant-id");
  const handle = card.getAttribute("handle");
  if (handle && variantId) {
    window.location.href = `/products/${handle}?variant=${variantId}`;
  }
});


/* ----------------------------------------------------------------------------
   3. Swatch "show more" button
   Clicking the "+N" button on a product card reveals the hidden swatches.
   -------------------------------------------------------------------------- */
document.addEventListener("DOMContentLoaded", function () {
  document.addEventListener("click", function (e) {
    const showMoreBtn = e.target.closest(".tm-show-more-button");
    if (showMoreBtn) {
      e.preventDefault();
      e.stopPropagation();
      const swatchList = showMoreBtn.closest(".product-card__swatch-list");
      if (swatchList) {
        swatchList.querySelectorAll(".hidden-swatch").forEach((swatch) => {
          swatch.style.display = "inline-flex";
        });
      }
      const wrapper = showMoreBtn.closest(".upsell-swatch-wrapper");
      if (wrapper) {
        wrapper.classList.add("force-scrollbar");
      }
      showMoreBtn.remove();
      return;
    }
  });
});


/* ----------------------------------------------------------------------------
   4. Accessory add-to-cart. Native forms share feedback and cart events.
   -------------------------------------------------------------------------- */
document.addEventListener("submit", async function (event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-ho-accessory-form]')) return;
  event.preventDefault();
  const button = form.querySelector('.tm-add-bag, .cd-add-bag');
  if (!button || form.dataset.adding === 'true' || button.disabled) return;

  const label = button.querySelector('.tm-add-text, .cd-add-bag__text');
  const status = form.querySelector('.ho-accessory-status');
  const defaultLabel = button.dataset.defaultLabel || 'Add to bag';
  const announce = (message, isError = false) => {
    if (!status) return;
    status.classList.toggle('sr-only', !isError);
    status.textContent = message;
  };
  form.dataset.adding = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.classList.remove('success');
  button.classList.add('loading');
  if (label) label.textContent = 'Adding…';
  announce('Adding to bag…');
  let added = false;

  try {
    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true, detail: { sections: sectionsToBundle }
    }));
    const formData = new FormData(form);
    formData.set('sections', sectionsToBundle.join(','));
    formData.set('sections_url', window.location.pathname + window.location.search);
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST', body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin'
    });
    const responseJson = await response.json();
    if (!response.ok) throw new Error(responseJson.description || 'Unable to add this item. Please try again.');
    added = true;
    button.classList.add('success');
    if (label) label.textContent = 'Added';
    announce('Added to bag.');

    const settings = window.themeVariables && window.themeVariables.settings || {};
    if (settings.cartType === 'page' || settings.pageType === 'cart') {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }
    const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
    if (!cartResponse.ok) throw new Error('Cart refresh failed');
    const cart = await cartResponse.json();
    cart.sections = responseJson.sections;
    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true, detail: { items: responseJson.items || [responseJson], cart }
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true, detail: { baseEvent: 'variant:add', cart }
    }));
  } catch (error) {
    if (added) {
      // The write succeeded: retain confirmation and refresh instead of inviting a duplicate add.
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } else {
      if (label) label.textContent = defaultLabel;
      announce(error.message || 'Unable to add this item. Please try again.', true);
      form.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true, detail: { error: error.message }
      }));
    }
  } finally {
    button.removeAttribute('aria-busy');
    button.classList.remove('loading');
    const reset = () => {
      delete form.dataset.adding;
      button.disabled = false;
      button.classList.remove('success');
      if (label) label.textContent = defaultLabel;
    };
    if (added) window.setTimeout(reset, 1600);
    else reset();
  }
});

// Accessory variant changes survive recommendation and cart section rerenders.
document.addEventListener('change', function (event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement) || !input.closest('[data-ho-accessory-form]')) return;
  if (input.name !== 'id') return;
  const option = input instanceof HTMLSelectElement ? input.options[input.selectedIndex] : input;
  const card = input.closest('product-card');
  if (!option || !card) return;
  const price = card.querySelector('[data-product-price]');
  if (price && option.dataset.variantPrice) {
    price.replaceChildren();
    const current = document.createElement('span');
    current.textContent = option.dataset.variantPrice;
    if (option.dataset.variantComparePrice) current.className = 'text-on-sale';
    price.append(current);
    if (option.dataset.variantComparePrice) {
      const previous = document.createElement('span');
      previous.className = 'text-subdued line-through';
      previous.textContent = option.dataset.variantComparePrice;
      price.append(' ', previous);
    }
  }
  const name = card.querySelector('.cd-product__variant-name, .variant-name-display');
  if (name && option.dataset.variantOption1) name.textContent = option.dataset.variantOption1;
  if (option.dataset.variantMedia) {
    try {
      const media = JSON.parse(option.dataset.variantMedia);
      const image = card.querySelector('.cd-product__img, .tm-image--upsell');
      if (image && media.preview_image) {
        image.src = media.preview_image.src.replace(/\?.*$/, '') + '?width=240';
        image.alt = media.alt || image.alt;
      }
    } catch (error) { /* Preserve the product image when a variant has no usable media. */ }
  }
});



/* ----------------------------------------------------------------------------
   5. Swatch change -> variant name label
   When a swatch radio changes on a product card, update the visible
   ".variant-name-display" text to the chosen option name.
   -------------------------------------------------------------------------- */
document.addEventListener("change", function (e) {
  const radio = e.target;
  if (radio.type === "radio" && radio.name === "id") {
    const variantName = radio.getAttribute("data-variant-option1");
    const productCard = radio.closest("product-card");
    if (productCard) {
      const variantDisplay = productCard.querySelector(".variant-name-display");
      if (variantDisplay && variantName && variantName !== "Default Title") {
        variantDisplay.textContent = variantName;
      }
    }
  }
});


/* ----------------------------------------------------------------------------
   6. window.showVariantMediaInGallery (swatch hover -> gallery image)
   MUST stay on `window` - it is called from inline onclick/markup elsewhere.
   -------------------------------------------------------------------------- */
window.showVariantMediaInGallery = function (productId, sectionId, posIndex) {
  var galleryId = "product-gallery-" + productId + "-" + sectionId;
  var carousel = document.getElementById(galleryId);
  if (carousel && typeof carousel.select === "function") {
    carousel.select(posIndex, { animate: false });
  }
};


/* ----------------------------------------------------------------------------
   7. Cart recommendations carousel
   Custom element + its prev/next buttons for the recs row in the cart drawer.
   NOTE: sections/cart-drawer.liquid renders an inline TWIN of this block on
   every page. The customElements.get() guards below and the `initialized`
   flags on both cart-drawer:open listeners let the two copies coexist; the
   whole-file IIFE (see header) keeps their class names from colliding.
   -------------------------------------------------------------------------- */
class CartRecsCarousel extends HTMLElement {
  constructor() {
    super();
    this.scrollStep = 300; // Adjust based on your card width
  }

  connectedCallback() {
    const carouselId = this.id;
    this.prevButton = document.querySelector(`button[aria-controls="${carouselId}"][is="cart-recs-prev"]`);
    this.nextButton = document.querySelector(`button[aria-controls="${carouselId}"][is="cart-recs-next"]`);

    if (this.prevButton) {
      this.prevButton.addEventListener("click", (e) => this.handleNavClick(e, "prev"));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener("click", (e) => this.handleNavClick(e, "next"));
    }

    this.style.overflowX = "auto";
    this.style.scrollBehavior = "smooth";
    this.addEventListener("scroll", () => this.updateButtonStates());
    this.updateButtonStates();

    // Defer update to allow content to render
    setTimeout(() => {
      this.updateButtonStates();
    }, 300);
  }

  handleNavClick(e, direction) {
    e.preventDefault();
    const scrollAmount = direction === "next" ? this.scrollStep : -this.scrollStep;
    this.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }

  updateButtonStates() {
    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.toggleAttribute("disabled", this.scrollLeft <= 0);
    const maxScroll = this.scrollWidth - this.clientWidth;
    this.nextButton.toggleAttribute("disabled", this.scrollLeft >= maxScroll);
  }
}

// Register custom elements (guarded so a double-load can't throw)
if (!window.customElements.get("cart-recs-carousel")) {
  window.customElements.define("cart-recs-carousel", CartRecsCarousel);
}
if (!window.customElements.get("cart-recs-prev")) {
  window.customElements.define("cart-recs-prev", class extends HTMLButtonElement {}, { extends: "button" });
}
if (!window.customElements.get("cart-recs-next")) {
  window.customElements.define("cart-recs-next", class extends HTMLButtonElement {}, { extends: "button" });
}

// Initialize carousels when the cart drawer opens
document.addEventListener("cart-drawer:open", () => {
  document.querySelectorAll("cart-recs-carousel").forEach((carousel) => {
    if (!carousel.initialized) {
      carousel.initialized = true;
      new CartRecsCarousel().connectedCallback.call(carousel);
    }
  });
});

// Re-initialize when a section reloads (theme editor only)
if (Shopify.designMode) {
  document.addEventListener("shopify:section:select", () => {
    setTimeout(() => {
      document.querySelectorAll("cart-recs-carousel").forEach((carousel) => {
        new CartRecsCarousel().connectedCallback.call(carousel);
      });
    }, 300);
  });
}


/* ----------------------------------------------------------------------------
   8. Sold-out product card fade
   Fades/greys product cards that carry a sold-out badge.
   (Switch to OPTION A inside if you'd rather hide them entirely.)
   -------------------------------------------------------------------------- */
document.addEventListener("DOMContentLoaded", function () {
  const soldOutBadges = document.querySelectorAll("sold-out-badge, .badge--sold-out");
  soldOutBadges.forEach(function (badge) {
    const productCard = badge.closest("product-card, .product-card");
    if (productCard) {
      // OPTION A: Completely hide the card
      // productCard.style.display = 'none';

      // OPTION B: Fade out the card (default)
      productCard.style.opacity = "0.4";
      productCard.style.filter = "grayscale(50%)";
    }
  });
});


/* ----------------------------------------------------------------------------
   9. Tepo customizer widget mover
   The Tepo app embed injects its widget at the bottom of the page; this moves
   it up next to the buy buttons. Self-disconnecting observer (max 10s).
   -------------------------------------------------------------------------- */
(function () {
  if (document.documentElement.dataset.customizationsPaused === "true") return;
  function moveTepo() {
    var widget = document.querySelector(".tcustomizer-widget");
    if (!widget) return false;
    var buyBlock = document.querySelector('[data-block-type="buy-buttons"]');
    if (!buyBlock) return false;
    if (widget.parentNode === buyBlock.parentNode && widget.nextElementSibling === buyBlock) return true;
    buyBlock.parentNode.insertBefore(widget, buyBlock);
    return true;
  }
  if (!moveTepo()) {
    var obs = new MutationObserver(function () {
      if (moveTepo()) obs.disconnect();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 10000);
  }
})();

/* ----------------------------------------------------------------------------
   10. Duo pair integrity (cart-wide orphan enforcement + load sweep)

   Duo planners are two Shopify products added together by the duo customizer
   (sections/duo-customizer.liquid). Each book line carries properties
   { _bundle: "Duo Bundle", "Duo Pair": "Duo N" }; accessory lines carry
   _duo_accessory and auto-remove with their parent book (parent_id).

   This block is the ONLY home of pair enforcement. The copy that used to sit
   inside duo-customizer.liquid ran only on customizer pages, so removing one
   book from the cart page (a full-reload /cart/change link, no JS event) or
   from the drawer on any other page orphaned its partner — three orders
   shipped as lone halves before this was caught (#75620, 2026-08-05).

   Two mechanisms, deliberately redundant:
     a) cart:change listener — covers the theme's ajax cart updates.
     b) page-load sweep — covers changes no event reaches: the cart page's
        full-reload remove links, other tabs, and partial adds (Shopify's
        multi-item /cart/add.js is NOT atomic: on 422 it can keep book 2;
        the customizer rolls those back itself, this is the safety net).
   After removing an orphan we reload on the cart page (server-rendered
   markup must be rebuilt) and dispatch cart:refresh everywhere else. The
   reload cannot loop: it only fires after a confirmed OK removal, and the
   next sweep finds no orphans.
   -------------------------------------------------------------------------- */
(function () {
  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }

  function onCartPage() {
    return location.pathname.indexOf(shopRoot() + "cart") === 0;
  }

  /* A duo BOOK line that carries no "Duo Pair" property never came from the
     customizer, so its partner book is not in the cart: added via a product
     page, quick-add, cart permalink, or the Shop app (orders #77686/#78038/
     #78058 checked out as lone halves this way, 2026-08-25/30). Title match
     because /cart.js has no tags; "Weekly & Daily Duo" contains "Daily Duo"
     so one needle covers both lines. Imperfect/printable products are
     deliberately sold solo and must stay exempt. */
  function isBareDuoBook(item) {
    var props = item.properties || {};
    if (props["Duo Pair"] || props["_duo_accessory"]) return false;
    var title = item.product_title || "";
    if (title.indexOf("Daily Duo") === -1) return false;
    if (title.indexOf("Imperfect") !== -1) return false;
    if (title.indexOf("Printable") !== -1 || title.indexOf("Download") !== -1) return false;
    return true;
  }

  function findOrphanKeys(cart) {
    var pairBooks = {};
    var keys = [];
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props["Duo Pair"] && !props["_duo_accessory"]) {
        (pairBooks[props["Duo Pair"]] = pairBooks[props["Duo Pair"]] || []).push(item);
      } else if (isBareDuoBook(item)) {
        keys.push(item.key);
      }
    });
    for (var pair in pairBooks) {
      if (pairBooks[pair].length === 1) keys.push(pairBooks[pair][0].key);
    }
    return keys;
  }

  async function removeOrphans(keys) {
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    var response = await fetch(shopRoot() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates })
    });
    if (!response.ok) return; // never reload on a failed removal (would loop)
    if (onCartPage()) {
      location.reload();
    } else {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }

  /* a) ajax cart changes — same guard flag the old section copy used, so a
     stale-cached customizer page can't double-bind alongside this listener */
  if (!window.__duoBundleEnforcer) {
    window.__duoBundleEnforcer = true;
    document.documentElement.addEventListener("cart:change", async function (event) {
      try {
        var cart = event.detail && event.detail.cart;
        if (!cart) return;
        var orphans = findOrphanKeys(cart);
        if (orphans.length) await removeOrphans(orphans);
      } catch (err) {
        console.error("[custom.js] duo pair enforcement (cart:change) failed:", err);
      }
    });
  }

  /* b) page-load sweep */
  if (!window.__duoOrphanSweep) {
    window.__duoOrphanSweep = true;
    var sweep = async function () {
      try {
        var cart = await (await fetch(shopRoot() + "cart.js")).json();
        var orphans = findOrphanKeys(cart);
        if (orphans.length) await removeOrphans(orphans);
      } catch (err) {
        console.error("[custom.js] duo orphan sweep failed:", err);
      }
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () { sweep(); });
    } else {
      sweep();
    }
  }
})();

/* ----------------------------------------------------------------------------
   11. HEADSTART free gift (cart watcher + in-cart picker)

   Multi-choice gift-with-purchase for the 2027 planner campaign. Products
   whose custom.free_gift metafield lists MORE THAN ONE product are cart-owned:
   the PDP adders (snippets/free-gift.liquid, sections/duo-customizer.liquid)
   skip them, and this block is the single writer for those gift lines.
   Pricing comes from the automatic BXGY discount "HEADSTART"; this code only
   manages cart lines, never prices.

   Inputs:
     - config JSON from snippets/free-gift-config.liquid (rendered by
       sections/cart-drawer.liquid and sections/main-cart.liquid), read from
       script[data-hs-gift-config]. Absent config = block idle.
     - /cart.js state. Campaign gift lines carry properties
       { _free_gift: "true", _hs: "1" } and deliberately NO
       _free_gift_parent_id, so cleanupOrphanedGifts() in cart-drawer.liquid
       ignores them (its filter requires the parent id).
     - cart attribute _hs_gift_state: "" | "offered" | "declined".

   Behaviour is contract-tested (hs_gift_core.test.js, 2026-08-10 session):
   auto-add the default gift colour-matched to the planner (FALLBACK_GIFT_COLOUR
   when the planner's colour has no 2026 counterpart), one per order, decline is
   sticky (re-add button renders via Liquid), gift is removed when no qualifying
   planner remains. A manually added horizontal counts as satisfied. The picker
   UI (style chips + colour swatches) is rendered into [data-hs-picker]
   containers from snippets/free-gift-picker.liquid.

   Timing matters here: the customer is looking at the drawer the theme opened
   for their planner, so the gift has to land in it fast. An add-to-cart sweeps
   with no debounce, asks Shopify to render the cart-drawer section as part of
   the gift's add request, and paints from that response before writing the
   (invisible) "offered" state. See paintDrawer.
   -------------------------------------------------------------------------- */
(function () {
  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }

  function onCartPage() {
    return location.pathname.indexOf(shopRoot() + "cart") === 0;
  }

  /* --- tested core (verbatim from hs_gift_core.js) ------------------------ */

  /* Which cart lines count as qualifying planner purchases. Uses the config's
     variant-level list when present: it mirrors the HEADSTART discount's
     variant-level buys list, which deliberately excludes pre-order colours
     (Wild Rose, Woodlands), so the watcher never gifts a cart the discount will
     refuse to zero at checkout. Falls back to product-level matching for cached
     drawer HTML rendered before eligibleVariants existed. */
  function eligibleMatcher(config) {
    var products = {};
    (((config || {}).eligible) || []).forEach(function (id) { products[String(id)] = true; });
    var variants = null;
    if (config && Object.prototype.toString.call(config.eligibleVariants) === "[object Array]") {
      variants = {};
      config.eligibleVariants.forEach(function (id) { variants[String(id)] = true; });
    }
    return function (item) {
      if (variants) return variants[String(item.variant_id)] === true;
      return products[String(item.product_id)] === true;
    };
  }

  /* Gift colour used when the planner's colour has no counterpart in the 2026
     range (every 2027-only colour, e.g. Natural Pearl). Deliberate neutral
     rather than "whichever variant sorts first", which drifts as colours sell
     out. Change this one string to change the fallback everywhere. */
  var FALLBACK_GIFT_COLOUR = "Charbon";

  function pickDefaultVariant(plannerColours, config) {
    if (!config || !config.gifts) return null;
    var wanted = (plannerColours || []).map(function (colour) {
      return String(colour || "").toLowerCase();
    });
    for (var g = 0; g < config.gifts.length; g++) {
      var available = (config.gifts[g].variants || []).filter(function (variant) {
        return variant.available;
      });
      if (!available.length) continue;
      for (var w = 0; w < wanted.length; w++) {
        for (var v = 0; v < available.length; v++) {
          if (String(available[v].colour || "").toLowerCase() === wanted[w]) return available[v].id;
        }
      }
      for (var f = 0; f < available.length; f++) {
        if (String(available[f].colour || "").toLowerCase() === FALLBACK_GIFT_COLOUR.toLowerCase()) {
          return available[f].id;
        }
      }
      return available[0].id;
    }
    return null;
  }

  /* The one-request PDP path is deliberately conservative: it only combines
     the purchased planner and gift when an authoritative cached cart is empty.
     Non-empty/unknown carts use ProductForm + this watcher, preserving one gift
     per order without a click-time cart read on the common empty-cart path. */
  function pickFastGiftVariant(cart, plannerVariantId, config) {
    if (!cart || Number(cart.item_count) !== 0) return null;
    if (String((cart.attributes || {})["_hs_gift_state"] || "")) return null;
    if (!config || !config.defaultGift || !Array.isArray(config.eligibleVariants)) return null;

    var eligible = {};
    config.eligibleVariants.forEach(function (id) { eligible[String(id)] = true; });
    if (!eligible[String(plannerVariantId)]) return null;

    var colour = "";
    (config.plannerVariants || []).forEach(function (variant) {
      if (String(variant.id) === String(plannerVariantId)) colour = variant.colour;
    });
    return pickDefaultVariant([colour], { gifts: [config.defaultGift] });
  }

  function computeGiftActions(cart, config) {
    var actions = [];
    if (!cart || !config) return actions;
    // US shoppers cannot receive the free 2026 planner offer. Clear a gift
    // carried over from another market before the shopper reaches checkout.
    if (config.countryExcluded === true) {
      var excludedGiftIds = (config.giftProductIds || []).map(String);
      (cart.items || []).forEach(function (item) {
        var props = item.properties || {};
        if (props['_hs'] === '1' ||
            (props['_free_gift'] === 'true' && excludedGiftIds.indexOf(String(item.product_id)) !== -1)) {
          actions.push({ type: 'remove', key: item.key });
        }
      });
      if ((cart.attributes || {})['_hs_gift_state']) actions.push({ type: 'setState', value: '' });
      return actions;
    }
    if (!config.gifts || !config.gifts.length) return actions;

    var isEligible = eligibleMatcher(config);
    var giftProductIds = {};
    config.gifts.forEach(function (gift) { giftProductIds[String(gift.productId)] = true; });

    var state = String((cart.attributes || {})["_hs_gift_state"] || "");
    var eligibleQty = 0;
    var plannerColours = [];
    var tagged = [];
    var satisfied = false;

    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      if (isEligible(item)) {
        eligibleQty += item.quantity;
        var colour = (item.variant_options && item.variant_options[0]) ||
          (item.variant_title ? String(item.variant_title).split(" / ")[0] : "");
        plannerColours.push(colour);
      }
      if (props["_hs"] === "1") tagged.push(item);
      if (giftProductIds[String(item.product_id)]) satisfied = true;
    });

    if (!eligibleQty) {
      tagged.forEach(function (item) { actions.push({ type: "remove", key: item.key }); });
      if (state) actions.push({ type: "setState", value: "" });
      return actions;
    }

    if (tagged.length) {
      /* Surplus tagged lines (a lost swap race, or anything else): keep the
         newest line, which /cart.js lists first, and remove the rest. */
      for (var t = 1; t < tagged.length; t++) actions.push({ type: "remove", key: tagged[t].key });
      if (actions.length) return actions;
      if (tagged[0].quantity > 1) actions.push({ type: "clamp", key: tagged[0].key });
      else if (!state) actions.push({ type: "setState", value: "offered" });
      return actions;
    }

    if (satisfied) return actions;
    if (state === "offered") return [{ type: "setState", value: "declined" }];
    if (state === "declined") return actions;

    var variantId = pickDefaultVariant(plannerColours, config);
    if (variantId != null) {
      actions.push({ type: "add", variantId: variantId });
      actions.push({ type: "setState", value: "offered" });
    }
    return actions;
  }

  /* --- config + cart helpers --------------------------------------------- */

  function readConfigRaw() {
    var el = document.querySelector("script[data-hs-gift-config]");
    return el ? el.textContent : "";
  }

  function readConfig() {
    var raw = readConfigRaw();
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (err) { return null; }
  }

  function readFastGiftConfig(form) {
    var el = form && form.querySelector && form.querySelector("script[data-hs-fast-gift-config]");
    if (!el || !el.textContent) return null;
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  }

  /* Convert the native ProductForm payload to the Cart AJAX API's JSON item
     shape. File properties cannot be represented safely in JSON, so those
     deliberately fall back to the theme's normal FormData submission. */
  function fastPlannerItem(form, variantId) {
    var formData = new FormData(form);
    var quantity = Number(formData.get("quantity") || 1);
    var item = {
      id: Number(variantId),
      quantity: isFinite(quantity) && quantity > 0 ? quantity : 1
    };
    var properties = {};
    var hasProperties = false;
    var hasFile = false;

    formData.forEach(function (value, key) {
      var property = String(key).match(/^properties\[(.*)\]$/);
      if (!property) return;
      if (typeof File !== "undefined" && value instanceof File) {
        if (value.size) hasFile = true;
        return;
      }
      properties[property[1]] = String(value);
      hasProperties = true;
    });
    if (hasFile) return null;
    if (hasProperties) item.properties = properties;

    var sellingPlan = formData.get("selling_plan");
    if (sellingPlan) item.selling_plan = Number(sellingPlan) || sellingPlan;
    return item;
  }

  /* A product add already carries freshly rendered cart-drawer HTML in
     event.detail.cart.sections. Read its config directly so the watcher can
     start before the theme's 0ms innerHTML swap puts that same script in the
     DOM. undefined = no bundled drawer was supplied; null = an authoritative
     bundled drawer with no eligible config. */
  function readConfigFromCartSections(cart) {
    var sections = cart && cart.sections;
    var html = sections && sections[drawerSectionId()];
    if (typeof html !== "string") return undefined;
    var match = html.match(/<script[^>]*data-hs-gift-config[^>]*>([\s\S]*?)<\/script>/);
    if (!match) return null;
    try { return JSON.parse(match[1]); } catch (err) { return undefined; }
  }

  function drawerSectionId() {
    var drawer = document.querySelector("cart-drawer");
    var section = drawer && drawer.closest('[id^="shopify-section-"]');
    return section ? section.id.replace("shopify-section-", "") : "cart-drawer";
  }

  /* Authoritative config when the DOM copy is missing or stale (page cache,
     or the drawer swapped to an empty-cart render mid-sweep): re-render the
     drawer section for THIS session's cart and read the config out of it. */
  async function fetchConfigFromSection() {
    try {
      var html = await (await fetch(shopRoot() + "?section_id=" + drawerSectionId())).text();
      var match = html.match(/<script[^>]*data-hs-gift-config[^>]*>([\s\S]*?)<\/script>/);
      return match ? JSON.parse(match[1]) : null;
    } catch (err) {
      return null;
    }
  }

  /* withDrawer asks Shopify to render the cart-drawer section as part of the
     add request, so the gift can be painted from that same response instead of
     costing a second round trip (see paintDrawer). */
  function addGift(variantId, withDrawer) {
    var body = { id: variantId, quantity: 1, properties: { _free_gift: "true", _hs: "1" } };
    if (withDrawer && !onCartPage()) {
      body.sections = drawerSectionId();
      body.sections_url = shopRoot();
    }
    return fetch(shopRoot() + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    });
  }

  /* Hand the drawer the HTML that came back with the gift add. The theme's own
     cart:change handler renders bundled sections with no delay when baseEvent
     is variant:add, which is the same path a normal add-to-cart uses; the
     alternative (cart:refresh) costs an extra section fetch before anything
     appears. The cart payload must be a REAL cart: other listeners read
     total_price (free shipping bar), item_count (cart count) and cache the
     object wholesale. Returns false if there was nothing to paint, so the
     caller can fall back to refreshUi. */
  function paintCart(cart, baseEvent) {
    var sections = cart && cart.sections;
    if (!sections || !sections[drawerSectionId()] || onCartPage()) return false;
    latestCart = cart;
    document.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: baseEvent || "variant:add",
        blockCartDrawerOpening: true,
        hsGiftPaint: true,
        cart: cart
      }
    }));
    return true;
  }

  async function paintDrawer(addResponseJson) {
    try {
      var sections = addResponseJson && addResponseJson.sections;
      if (!sections || !sections[drawerSectionId()] || onCartPage()) return false;
      var cart = await (await fetch(shopRoot() + "cart.js")).json();
      cart.sections = sections;
      return paintCart(cart, "variant:add");
    } catch (err) {
      return false;
    }
  }

  function cartUpdate(payload) {
    return fetch(shopRoot() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload)
    });
  }

  function refreshUi() {
    if (onCartPage()) { location.reload(); return; }
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    fetch(shopRoot() + "cart.js").then(function (response) { return response.json(); })
      .then(function (cart) {
        document.querySelectorAll("cart-count").forEach(function (el) {
          el.textContent = cart.item_count;
        });
      }).catch(function () {});
  }

  function plannerColoursIn(cart, config) {
    var isEligible = eligibleMatcher(config);
    var colours = [];
    (cart.items || []).forEach(function (item) {
      if (!isEligible(item)) return;
      var colour = (item.variant_options && item.variant_options[0]) ||
        (item.variant_title ? String(item.variant_title).split(" / ")[0] : "");
      colours.push(colour);
    });
    return colours;
  }

  /* Add the best candidate; on 422 (sold out since render) exclude and retry.
     Resolves to the add response JSON (which carries the bundled drawer HTML)
     so the caller can paint from it, or null when nothing could be added. */
  async function addBestGift(cart, config) {
    var excluded = {};
    var colours = plannerColoursIn(cart, config);
    for (var attempt = 0; attempt < 3; attempt++) {
      var filtered = {
        eligible: config.eligible,
        gifts: (config.gifts || []).map(function (gift) {
          return {
            productId: gift.productId,
            variants: (gift.variants || []).filter(function (v) { return !excluded[v.id]; })
          };
        })
      };
      var variantId = pickDefaultVariant(colours, filtered);
      if (variantId == null) return null;
      var response = await addGift(variantId, true);
      if (response.ok) {
        try { return await response.json(); } catch (err) { return {}; }
      }
      excluded[variantId] = true;
    }
    console.warn("[custom.js] HEADSTART gift: add gave up, variants unavailable");
    return null;
  }

  /* --- re-add row visibility ----------------------------------------------- */

  /* The re-add row's visibility is asserted from watcher state rather than
     trusted to Liquid alone: the drawer HTML bundled with a gift REMOVAL was
     rendered while the state was still "offered", and when that stale swap
     lands after our "declined" write the row stays hidden until a manual
     refresh (Tia's 08-10 repro). The row is always in the DOM (hidden attr);
     sweeps decide, mutations re-assert. */
  var readdEligible = false;

  function applyReaddVisibility() {
    document.querySelectorAll("[data-hs-readd-row]").forEach(function (row) {
      row.hidden = !readdEligible;
    });
  }

  /* --- in-flight placeholder ---------------------------------------------- */

  /* The theme opens the drawer as soon as the planner lands, and the gift is
     still a round trip away. Without this the drawer looks like nothing
     happened, which is exactly how the feature reads as broken. Paints a row
     for the gift we are about to add, in the drawer's own line-item markup;
     any drawer re-render (including the real gift landing) replaces it. */
  function giftVariantById(config, variantId) {
    var found = null;
    (config.gifts || []).forEach(function (gift) {
      (gift.variants || []).forEach(function (v) {
        if (String(v.id) === String(variantId)) found = { gift: gift, variant: v };
      });
    });
    return found;
  }

  function removePendingGift() {
    var pending = document.querySelector("[data-hs-pending]");
    if (pending) pending.remove();
  }

  function showPendingGift(cart, config) {
    try {
      var list = document.querySelector(".cart-drawer__line-items");
      if (!list || document.querySelector("[data-hs-picker]") || document.querySelector("[data-hs-pending]")) return false;
      var variantId = pickDefaultVariant(plannerColoursIn(cart, config), config);
      var match = variantId == null ? null : giftVariantById(config, variantId);
      if (!match) return false;

      var row = document.createElement("line-item");
      row.className = "tm-line-item line-item hs-gift-pending";
      row.setAttribute("data-hs-pending", "");
      row.setAttribute("aria-live", "polite");

      var mediaWrap = document.createElement("div");
      mediaWrap.className = "tm-line-item-media-wrapper";
      if (match.variant.img) {
        var img = document.createElement("img");
        img.className = "tm-line-item-media"; // theme class, else it renders undersized
        img.src = match.variant.img;
        img.alt = "";
        mediaWrap.appendChild(img);
      }

      var content = document.createElement("div");
      content.className = "tm-line-item-content";
      var header = document.createElement("div");
      header.className = "tm-line-item-header";
      var title = document.createElement("span");
      title.className = "tm-line-item-title";
      title.textContent = match.gift.title || match.gift.label || "Your free 2026 planner";
      header.appendChild(title);
      content.appendChild(header);

      var colour = document.createElement("p");
      colour.className = "tm-line-item-variant";
      colour.textContent = match.variant.colour || "";
      content.appendChild(colour);

      var caption = document.createElement("p");
      caption.className = "hs-gift-caption";
      caption.textContent = "Adding your free 2026 planner";
      content.appendChild(caption);

      row.appendChild(mediaWrap);
      row.appendChild(content);
      list.insertBefore(row, list.firstChild);
      return true;
    } catch (err) {
      /* cosmetic only: never let the placeholder break the sweep */
      return false;
    }
  }

  /* --- the sweep ---------------------------------------------------------- */

  var sweeping = false;
  var queued = false;
  var queuedCart = null;
  var latestCart = null;
  var suspended = false; // true while a picker swap is mid-flight
  var fastSubmitting = false;

  /* Empty-cart PDP fast path: add the purchased planner and exactly ONE gift
     in Shopify's first request. Once the cart has anything in it, the native
     ProductForm runs unchanged and the watcher preserves the single gift. */
  async function submitFastGift(form, plannerItem, giftVariantId) {
    var submitButtons = Array.prototype.filter.call(form.elements || [], function (element) {
      return element && element.type === "submit";
    });
    submitButtons.forEach(function (button) { button.setAttribute("aria-busy", "true"); });
    fastSubmitting = true;

    var sections = ["variant-added"];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections: sections }
    }));
    var body = {
      items: [
        plannerItem,
        {
          id: Number(giftVariantId),
          quantity: 1,
          properties: { _free_gift: "true", _hs: "1" }
        }
      ],
      sections: sections.join(","),
      sections_url: shopRoot() + "variants/" + plannerItem.id
    };

    try {
      var response = await fetch(shopRoot() + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify(body)
      });
      var responseJson = await response.json();

      if (!response.ok) {
        latestCart = null;
        form.dispatchEvent(new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: responseJson.description || responseJson.message || "Could not add to cart." }
        }));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return;
      }

      window.__hsFastGiftLast = {
        plannerVariantId: plannerItem.id,
        giftVariantId: Number(giftVariantId)
      };
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      if (settings.cartType === "page" || settings.pageType === "cart") {
        window.location.href = shopRoot() + "cart";
        return;
      }

      var cartResponse = await fetch(shopRoot() + "cart.js");
      if (!cartResponse.ok) throw new Error("Cart read failed after bundled add");
      var cart = await cartResponse.json();
      cart.sections = responseJson.sections;
      latestCart = cart;

      /* Mirror ProductForm's public events so the cart drawer, count, quick-buy
         UI and the fallback gift watcher all keep their native behaviour. */
      if (cart.sections) {
        form.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.items || [],
            cart: cart
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", cart: cart }
        }));
      } else {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (err) {
      latestCart = null;
      console.error("[custom.js] HEADSTART bundled add failed:", err);
      form.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: { error: "Could not add to cart. Please try again." }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } finally {
      fastSubmitting = false;
      submitButtons.forEach(function (button) { button.removeAttribute("aria-busy"); });
    }
  }

  function onFastGiftSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[is="product-form"]')) return;
    var config = readFastGiftConfig(form);
    if (!config) return;

    /* Ignore a double-click while the first two-item request is in flight. */
    if (fastSubmitting) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    var submitter = event.submitter;
    if (submitter && (submitter.name === "checkout" ||
      (submitter.closest && submitter.closest(".shopify-payment-button")))) return;
    if (form.checkValidity && !form.checkValidity()) return;

    var formData = new FormData(form);
    var plannerVariantId = formData.get("id");
    var giftVariantId = pickFastGiftVariant(latestCart, plannerVariantId, config);
    if (giftVariantId == null) return;
    var plannerItem = fastPlannerItem(form, plannerVariantId);
    if (!plannerItem) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    submitFastGift(form, plannerItem, giftVariantId);
  }

  async function sweep(cartHint) {
    if (sweeping || suspended) {
      queued = true;
      if (cartHint && cartHint.items) queuedCart = cartHint;
      return;
    }
    sweeping = true;
    var sweepMutated = false;
    try {
      var cart = cartHint && cartHint.items ? cartHint : null;
      if (!cart) cart = await (await fetch(shopRoot() + "cart.js")).json();
      latestCart = cart;
      var bundledConfig = cartHint ? readConfigFromCartSections(cartHint) : undefined;
      var config = bundledConfig === undefined ? readConfig() : bundledConfig;
      if (!config) {
        /* No config in the DOM. If we have tagged lines or leftover state,
           the cart may have just lost its last planner (config renders only
           for eligible carts) OR this page's cached HTML lacks it. Ask the
           server for the authoritative drawer render before deciding. */
        var strays = (cart.items || []).some(function (item) {
          return (item.properties || {})["_hs"] === "1";
        });
        var leftoverState = String((cart.attributes || {})["_hs_gift_state"] || "");
        if (strays || leftoverState) {
          config = await fetchConfigFromSection();
          if (!config) {
            /* Authoritatively ineligible: same as computeGiftActions with
               zero eligible lines: drop our lines, clear our state. */
            config = { eligible: [], gifts: [{ productId: -1, variants: [] }] };
          }
        }
      }
      if (config) {
        var actions = computeGiftActions(cart, config);

        /* Post-sweep UI state for the re-add row, derived before the mutations
           land so the row can flip the instant the decline is decided. */
        var isEligible = eligibleMatcher(config);
        var eligibleQty = 0;
        var taggedCount = 0;
        (cart.items || []).forEach(function (item) {
          if (isEligible(item)) eligibleQty += item.quantity;
          if ((item.properties || {})["_hs"] === "1") taggedCount++;
        });
        var finalState = String((cart.attributes || {})["_hs_gift_state"] || "");
        actions.forEach(function (action) {
          if (action.type === "setState") finalState = action.value;
          if (action.type === "add") taggedCount++;
        });
        if (!eligibleQty) taggedCount = 0;
        readdEligible = !!eligibleQty && finalState === "declined" && !taggedCount;
        applyReaddVisibility();

        if (actions.length) {
          var mutated = false;
          var painted = false;
          var updates = {};
          var attributes = null;
          for (var i = 0; i < actions.length; i++) {
            var action = actions[i];
            if (action.type === "add") {
              if (!showPendingGift(cart, config)) {
                /* The immediate sweep can beat CartDrawer's 0ms DOM swap.
                   Retry just behind it while Shopify processes the gift add. */
                setTimeout(function () { showPendingGift(cart, config); }, 40);
              }
              var added = await addBestGift(cart, config);
              if (added) {
                mutated = true;
                sweepMutated = true;
                /* Paint now, before the state write below: "offered" is
                   invisible to the customer, so making them wait another round
                   trip for it only delays the gift appearing. */
                painted = await paintDrawer(added);
                if (painted) {
                  /* The theme swaps the drawer HTML on a 0ms timeout for
                     variant:add; hydrate the chips right behind it instead of
                     waiting for the observer's 120ms debounce (chips popping
                     in late read as flicker). */
                  setTimeout(renderPickers, 60);
                }
              }
            } else if (action.type === "remove") {
              updates[action.key] = 0;
            } else if (action.type === "clamp") {
              updates[action.key] = 1;
            } else if (action.type === "setState") {
              attributes = { _hs_gift_state: action.value };
            }
          }
          var payload = {};
          if (Object.keys(updates).length) { payload.updates = updates; }
          if (attributes) { payload.attributes = attributes; }
          if (payload.updates || payload.attributes) {
            var response = await cartUpdate(payload);
            if (response.ok) {
              sweepMutated = true;
              if (payload.updates) {
                mutated = true;
                try { latestCart = await response.clone().json(); } catch (err) { /* optional cache */ }
              }
            }
          }
          /* "offered" is invisible; declined/cleared re-render the Liquid row.
             A paint already rendered the drawer, so only fall back to the
             slower refresh when we did not paint, or when line updates landed
             after it and left the painted HTML stale. */
          var stateChangedUi = attributes && attributes._hs_gift_state !== "offered";
          if ((mutated || stateChangedUi) && (!painted || payload.updates)) refreshUi();
        }
      }
    } catch (err) {
      console.error("[custom.js] HEADSTART gift sweep failed:", err);
    } finally {
      sweeping = false;
      /* A successful add repaints the drawer, which drops the placeholder with
         it; this covers the failed-add and nothing-to-do paths. */
      removePendingGift();
      if (queued) {
        queued = false;
        /* Any queued event may carry a snapshot from before this sweep's cart
           mutation. Reusing that stale no-gift cart was the duplicate-gift
           race when customers added a second 2027 planner quickly. */
        var nextCart = sweepMutated ? null : queuedCart;
        queuedCart = null;
        schedule(nextCart ? 0 : undefined, nextCart);
      }
    }
  }

  var sweepTimer = null;
  var sweepDueAt = 0;
  var scheduledCart = null;
  /* 350ms coalesces the burst of events a cart mutation fires. An add-to-cart
     is the one case where the customer is watching an empty-looking drawer, so
     it sweeps immediately; the re-entrancy guard, not the delay, is what keeps
     concurrent sweeps safe. */
  function schedule(delay, cartHint) {
    if (cartHint && cartHint.items) scheduledCart = cartHint;
    var wait = typeof delay === "number" ? delay : 350;
    var dueAt = Date.now() + wait;

    /* Never let a later coalescing event postpone work already scheduled for
       sooner. ProductForm emits variant:add and then cart:change in the same
       task; the old clear-and-reset logic turned the intended 0ms sweep back
       into 350ms on every planner add. */
    if (sweepTimer !== null && sweepDueAt <= dueAt) return;

    clearTimeout(sweepTimer);
    sweepDueAt = dueAt;
    sweepTimer = setTimeout(function () {
      sweepTimer = null;
      sweepDueAt = 0;
      var nextCart = scheduledCart;
      scheduledCart = null;
      sweep(nextCart);
    }, wait);
  }

  /* --- picker rendering ---------------------------------------------------- */

  function findColour(config, variantId) {
    var colour = "";
    (config.gifts || []).forEach(function (gift) {
      (gift.variants || []).forEach(function (v) {
        if (String(v.id) === String(variantId)) colour = v.colour;
      });
    });
    return colour;
  }

  var lastRenderSignature = "";

  function renderPickers() {
    /* Asserted here too: this runs behind every drawer mutation, so a stale
       server render can never leave the re-add row in the wrong state. */
    applyReaddVisibility();
    var pickers = document.querySelectorAll("[data-hs-picker]");
    if (!pickers.length) { lastRenderSignature = ""; return; }
    var config = readConfig();
    if (!config || !config.gifts || !config.gifts.length) return;

    var signature = readConfigRaw();
    pickers.forEach(function (picker) {
      var chips = picker.querySelector("[data-hs-chips]");
      signature += "|" + picker.getAttribute("data-variant-id") + ":" + (chips ? chips.childElementCount : -1);
    });
    if (signature === lastRenderSignature) return;
    lastRenderSignature = signature;

    pickers.forEach(function (picker) {
      if (!picker.hasAttribute("aria-busy")) picker.setAttribute("aria-busy", "false");
      var chipsHost = picker.querySelector("[data-hs-chips]");
      var swatchHost = picker.querySelector("[data-hs-swatches]");
      if (!chipsHost || !swatchHost) return;

      var currentVariantId = String(picker.getAttribute("data-variant-id") || "");
      var currentProduct = null;
      config.gifts.forEach(function (gift) {
        (gift.variants || []).forEach(function (v) {
          if (String(v.id) === currentVariantId) currentProduct = gift;
        });
      });
      if (!currentProduct) currentProduct = config.gifts[0];

      chipsHost.textContent = "";
      var styleLabel = document.createElement("label");
      styleLabel.className = "hs-gift-style";
      var styleCaption = document.createElement("span");
      styleCaption.className = "hs-gift-style-label";
      styleCaption.textContent = "Choose your free planner";
      var styleSelect = document.createElement("select");
      styleSelect.className = "hs-gift-select";
      styleSelect.setAttribute("data-hs-style-select", "");
      styleSelect.setAttribute("aria-label", "Choose your free planner");
      config.gifts.forEach(function (gift) {
        var anyAvailable = (gift.variants || []).some(function (v) { return v.available; });
        if (!anyAvailable && gift !== currentProduct) return;
        var option = document.createElement("option");
        option.value = String(gift.productId);
        option.textContent = "2026 " + gift.label;
        option.selected = gift === currentProduct;
        option.disabled = !anyAvailable;
        styleSelect.appendChild(option);
      });
      styleLabel.appendChild(styleCaption);
      styleLabel.appendChild(styleSelect);
      chipsHost.appendChild(styleLabel);

      swatchHost.textContent = "";
      (currentProduct.variants || []).forEach(function (v) {
        var isCurrent = String(v.id) === currentVariantId;
        if (!v.available && !isCurrent) return;
        var swatch = document.createElement("button");
        swatch.type = "button";
        swatch.className = "hs-swatch" + (isCurrent ? " is-active" : "");
        swatch.setAttribute("data-hs-swatch", v.id);
        swatch.setAttribute("aria-label", v.colour);
        swatch.setAttribute("aria-pressed", isCurrent ? "true" : "false");
        swatch.title = v.colour;
        if (v.hex) {
          /* flat theme-style colour dot (native option-value swatch colour) */
          swatch.style.backgroundColor = v.hex;
        } else {
          var img = document.createElement("img");
          img.src = v.img;
          img.alt = "";
          img.loading = "lazy";
          swatch.appendChild(img);
        }
        swatchHost.appendChild(swatch);
      });

      var colourLabel = picker.querySelector("[data-hs-colour]");
      if (colourLabel) colourLabel.textContent = findColour(config, currentVariantId);
    });
  }

  var renderTimer = null;
  function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(renderPickers, 120);
  }

  /* --- picker interactions ------------------------------------------------- */

  function setPressed(controls, active) {
    controls.forEach(function (control) {
      var selected = control === active;
      control.classList.toggle("is-active", selected);
      control.setAttribute("aria-pressed", selected ? "true" : "false");
    });
  }

  function beginPickerFeedback(picker, targetVariantId, trigger) {
    var controls = Array.prototype.slice.call(picker.querySelectorAll("[data-hs-chip], [data-hs-swatch], [data-hs-style-select]"));
    var activeControls = controls.filter(function (control) { return control.classList.contains("is-active"); });
    var disabled = controls.map(function (control) { return control.disabled; });
    var styleSelect = picker.querySelector("[data-hs-style-select]");
    var previousStyleValue = picker.getAttribute("data-product-id");
    var variantLabel = picker.closest(".tm-line-item-content");
    variantLabel = variantLabel && variantLabel.querySelector(".tm-line-item-variant");
    var previousVariantLabel = variantLabel ? variantLabel.textContent : "";
    var status = picker.querySelector("[data-hs-status]");
    if (!status) {
      status = document.createElement("span");
      status.className = "hs-gift-status";
      status.setAttribute("data-hs-status", "");
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      var caption = picker.querySelector(".hs-gift-caption");
      if (caption) caption.appendChild(status);
    }
    var previousStatus = status ? status.textContent : "";
    var match = giftVariantById(readConfig() || { gifts: [] }, targetVariantId);
    var targetLabel = match && match.variant ? match.variant.colour : "your selection";
    if (trigger && (trigger.hasAttribute("data-hs-chip") || trigger.hasAttribute("data-hs-style-select")) && match && match.gift) {
      targetLabel = match.gift.label + (match.variant.colour ? " in " + match.variant.colour : "");
    }

    picker.classList.add("hs-busy");
    picker.setAttribute("aria-busy", "true");
    controls.forEach(function (control) {
      control.disabled = true;
      control.setAttribute("aria-disabled", "true");
    });
    if (trigger) trigger.classList.add("is-pending");

    if (trigger && trigger.hasAttribute("data-hs-chip")) {
      setPressed(Array.prototype.slice.call(picker.querySelectorAll("[data-hs-chip]")), trigger);
    }
    var targetSwatch = picker.querySelector('[data-hs-swatch="' + String(targetVariantId) + '"]');
    if (targetSwatch) {
      setPressed(Array.prototype.slice.call(picker.querySelectorAll("[data-hs-swatch]")), targetSwatch);
      targetSwatch.classList.add("is-pending");
    }
    if (variantLabel && match && match.variant && match.variant.colour) {
      variantLabel.textContent = match.variant.colour;
    }
    if (status) {
      status.classList.add("is-updating");
      status.textContent = "Updating to " + targetLabel + "...";
    }

    return {
      controls: controls,
      activeControls: activeControls,
      disabled: disabled,
      styleSelect: styleSelect,
      previousStyleValue: previousStyleValue,
      variantLabel: variantLabel,
      previousVariantLabel: previousVariantLabel,
      status: status,
      previousStatus: previousStatus,
      targetLabel: targetLabel
    };
  }

  function finishPickerFeedback(picker, feedback, restore, message) {
    if (!picker || !picker.isConnected || !feedback) return;
    picker.classList.remove("hs-busy");
    picker.setAttribute("aria-busy", "false");
    feedback.controls.forEach(function (control, index) {
      control.disabled = feedback.disabled[index];
      control.removeAttribute("aria-disabled");
      control.classList.remove("is-pending");
      if (restore && !control.hasAttribute("data-hs-style-select")) {
        var wasActive = feedback.activeControls.indexOf(control) !== -1;
        control.classList.toggle("is-active", wasActive);
        control.setAttribute("aria-pressed", wasActive ? "true" : "false");
      }
    });
    if (restore && feedback.styleSelect) feedback.styleSelect.value = feedback.previousStyleValue;
    if (restore && feedback.variantLabel) feedback.variantLabel.textContent = feedback.previousVariantLabel;
    if (feedback.status) {
      feedback.status.classList.remove("is-updating");
      feedback.status.textContent = message || feedback.previousStatus;
      if (message) {
        setTimeout(function () {
          if (feedback.status.isConnected && feedback.status.textContent === message) {
            feedback.status.textContent = "";
          }
        }, 4000);
      }
    }
  }

  async function currentCartForPicker(picker) {
    var pickerKey = picker.getAttribute("data-line-key") || "";
    var cacheMatches = latestCart && (latestCart.items || []).some(function (item) {
      return item.key === pickerKey && (item.properties || {})["_hs"] === "1";
    });
    if (cacheMatches) return latestCart;
    var cart = await (await fetch(shopRoot() + "cart.js")).json();
    latestCart = cart;
    return cart;
  }

  /* Swap the gift line to another style/colour. Reuse the last authoritative
     cart only when it still contains the picker's exact line key; otherwise
     refetch instead of trusting stale data attributes. Acting on a stale line
     key made chip clicks silent no-ops (08-10 report: Cloth Flex chip "did
     nothing"; couldn't return to Hardcover after picking A5). */
  async function swapGift(picker, targetVariantId, trigger) {
    if (suspended) return; // one swap at a time: rapid clicks caused surplus gift lines
    if (!targetVariantId) return;
    suspended = true;
    var feedback = beginPickerFeedback(picker, targetVariantId, trigger);
    var completed = false;
    var refreshing = false;
    var addedNewLine = false;
    try {
      var cart = await currentCartForPicker(picker);
      var tagged = (cart.items || []).filter(function (item) {
        return (item.properties || {})["_hs"] === "1";
      });
      var alreadyTarget = tagged.some(function (item) {
        return String(item.variant_id) === String(targetVariantId);
      });
      if (alreadyTarget) {
        completed = true;
      } else if (tagged.length) {
        var response = await addGift(targetVariantId);
        if (response.ok) {
          addedNewLine = true;
          var updates = {};
          tagged.forEach(function (item) { updates[item.key] = 0; }); // clears surplus too
          var payload = { updates: updates };
          if (!onCartPage()) {
            payload.sections = drawerSectionId();
            payload.sections_url = shopRoot();
          }
          var updateResponse = await cartUpdate(payload);
          if (!updateResponse.ok) throw new Error("gift swap removal failed: " + updateResponse.status);
          var updatedCart = await updateResponse.json();
          latestCart = updatedCart;
          completed = true;
          if (onCartPage()) {
            refreshing = true;
            location.reload();
          } else if (paintCart(updatedCart, "line-item:change")) {
            /* CartDrawer applies its line-item HTML on a 0ms timer. Keep the
               old picker locked through that swap, then hydrate the new one. */
            await new Promise(function (resolve) { setTimeout(resolve, 40); });
            setTimeout(renderPickers, 60);
          } else {
            refreshing = true;
            refreshUi();
          }
        } else {
          console.warn("[custom.js] HEADSTART gift: swap add failed", response.status);
          refreshing = true;
          if (feedback.status) feedback.status.textContent = "That option just sold out. Refreshing options...";
          refreshUi();
        }
      }
    } catch (err) {
      console.error("[custom.js] HEADSTART gift swap failed:", err);
      if (addedNewLine) {
        /* The add landed but removing the previous line did not. Let the
           watcher collapse the surplus immediately, and keep the UI locked
           until the authoritative drawer refresh replaces it. */
        refreshing = true;
        if (feedback.status) feedback.status.textContent = "Finishing your update...";
        refreshUi();
      }
    } finally {
      suspended = false;
      if (refreshing) {
        setTimeout(function () {
          finishPickerFeedback(picker, feedback, !completed, completed ? "Updated" : "Please try again.");
        }, 2500);
      } else {
        finishPickerFeedback(
          picker,
          feedback,
          !completed,
          completed ? "Updated to " + feedback.targetLabel : "Could not update. Please try again."
        );
      }
      if (!completed || addedNewLine && refreshing) schedule(0);
    }
  }

  /* A colour/style swap adds the replacement before removing the old gift.
     Keep a checkout click in the cart until that operation and any surplus
     cleanup finish, then continue through the native checkout control. */
  var giftCheckoutPending = false;
  async function waitForGiftCheckout(event) {
    if (!suspended && !giftCheckoutPending) return;
    var target = event.type === "submit" ? event.submitter : event.target;
    var control = target && target.closest && target.closest('[name="checkout"], a[href*="/checkout"]');
    if (!control) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (giftCheckoutPending) return;
    giftCheckoutPending = true;
    var inDrawer = !!control.closest("cart-drawer");
    var href = control.tagName === "A" ? control.href : null;
    var previousBusy = control.getAttribute("aria-busy");
    control.setAttribute("aria-busy", "true");
    var ready = false;
    try {
      var deadline = Date.now() + 15000;
      while (Date.now() < deadline) {
        await new Promise(function (resolve) { setTimeout(resolve, 100); });
        if (suspended || sweeping) continue;
        var response = await fetch(shopRoot() + "cart.js", { cache: "no-store" });
        if (!response.ok) throw new Error("Cart could not be checked before checkout");
        var cart = await response.json();
        var gifts = (cart.items || []).filter(function (item) { return (item.properties || {})._hs === "1"; });
        if (gifts.length > 1 || gifts.some(function (item) { return item.quantity > 1; })) {
          await sweep(cart);
          continue;
        }
        if (suspended || sweeping) continue;
        ready = true;
        break;
      }
    } catch (err) {
      console.warn("[custom.js] HEADSTART checkout waits for gift update:", err);
    } finally {
      giftCheckoutPending = false;
      if (previousBusy === null) control.removeAttribute("aria-busy");
      else control.setAttribute("aria-busy", previousBusy);
    }
    if (ready) {
      if (href) { location.assign(href); return; }
      var currentControl = control.isConnected ? control : document.querySelector(inDrawer ? 'cart-drawer [name="checkout"]' : 'form [name="checkout"]');
      if (currentControl) currentControl.click();
    } else {
      document.querySelectorAll("[data-hs-status]").forEach(function (status) {
        status.textContent = "Your free planner is still updating. Please try checkout again.";
      });
    }
  }

  function chipTargetVariant(config, productId, currentColour) {
    var gift = null;
    (config.gifts || []).forEach(function (g) {
      if (String(g.productId) === String(productId)) gift = g;
    });
    if (!gift) return null;
    var available = (gift.variants || []).filter(function (v) { return v.available; });
    if (!available.length) return null;
    for (var i = 0; i < available.length; i++) {
      if (String(available[i].colour || "").toLowerCase() === String(currentColour || "").toLowerCase()) {
        return available[i].id;
      }
    }
    return available[0].id;
  }

  /* --- wiring (guarded once) ----------------------------------------------- */

  if (!window.__hsGiftWatcher) {
    window.__hsGiftWatcher = true;

    /* Capture beats ProductForm's target listener only on the conservative
       empty-cart path; every other submit remains entirely theme-owned. */
    document.addEventListener("submit", onFastGiftSubmit, true);
    document.addEventListener("click", waitForGiftCheckout, true);
    document.addEventListener("submit", waitForGiftCheckout, true);

    document.documentElement.addEventListener("cart:change", function (event) {
      var detail = event.detail || {};
      var cart = detail.cart && detail.cart.items ? detail.cart : null;
      if (cart) latestCart = cart;
      if (detail.hsGiftPaint) return;
      schedule(detail.baseEvent === "variant:add" ? 0 : undefined, cart);
    });
    document.addEventListener("cart:refresh", function () {
      latestCart = null;
      schedule();
    });
    document.addEventListener("variant:add", function (event) {
      var cart = event.detail && event.detail.cart && event.detail.cart.items ? event.detail.cart : null;
      if (cart) latestCart = cart;
      schedule(0, cart);
    });
    document.addEventListener("cart-drawer:open", function () { schedule(0); });

    document.addEventListener("change", function (event) {
      var select = event.target.closest && event.target.closest("[data-hs-style-select]");
      if (!select) return;
      var picker = select.closest("[data-hs-picker]");
      var config = readConfig();
      if (!picker || !config) return;
      var previousProductId = picker.getAttribute("data-product-id");
      if (suspended) {
        select.value = previousProductId;
        return;
      }
      if (String(select.value) === String(previousProductId)) return;
      var currentColour = findColour(config, picker.getAttribute("data-variant-id"));
      var target = chipTargetVariant(config, select.value, currentColour);
      if (target) swapGift(picker, target, select);
      else select.value = previousProductId;
    });

    document.addEventListener("click", function (event) {
      var chip = event.target.closest("[data-hs-chip]");
      var swatch = event.target.closest("[data-hs-swatch]");
      var readd = event.target.closest("[data-hs-readd]");
      if (!chip && !swatch && !readd) return;
      event.preventDefault();
      var config = readConfig();
      if (!config) return;
      if (readd) {
        readdEligible = false;
        applyReaddVisibility(); // instant feedback; the sweep's pending row takes over
        cartUpdate({ attributes: { _hs_gift_state: "" } }).then(function () { sweep(); });
        return;
      }
      var picker = (chip || swatch).closest("[data-hs-picker]");
      if (!picker) return;
      if ((chip || swatch).classList.contains("is-active")) return;
      if (swatch) {
        swapGift(picker, swatch.getAttribute("data-hs-swatch"), swatch);
        return;
      }
      var currentColour = findColour(config, picker.getAttribute("data-variant-id"));
      var target = chipTargetVariant(config, chip.getAttribute("data-hs-chip"), currentColour);
      if (target) swapGift(picker, target, chip);
    });

    /* Drawer re-renders replace the config script without firing cart events
       (e.g. a bare cart:refresh whose section fetch lands after our sweep),
       so a config CONTENT change must itself schedule a sweep. */
    var lastConfigRaw = null;
    var observer = new MutationObserver(function () {
      applyReaddVisibility(); // undebounced: a late stale swap must not hide the row
      scheduleRender();
      var raw = readConfigRaw();
      if (raw !== lastConfigRaw) {
        lastConfigRaw = raw;
        if (raw) schedule();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    var boot = function () {
      lastConfigRaw = readConfigRaw();
      schedule();
      scheduleRender();
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  }
})();

/* ----------------------------------------------------------------------------
   12. Spec glossary popover (tappable spec terms on product cards)

   snippets/product-spec-line.liquid renders a spec as
   <button class="product-spec-line__item spec-glossary-trigger"
           data-spec-term="..." data-spec-def="...">
   whenever a spec_glossary metaobject entry exists for it (matched by
   handle; see the snippet header). This block owns the popover: ONE shared
   element appended to <body>, filled from the tapped trigger's data
   attributes, positioned under the trigger and clamped to the viewport.

   Closes on: tapping the same trigger again, tapping anywhere else, Esc,
   any scroll (capture phase, so inner scrollers count), and resize.
   Styles live in custom.css ("SPEC GLOSSARY POPOVER").

   preventDefault + stopPropagation on the trigger tap matter: the spec line
   also renders in contexts where an ancestor link or a card-level click
   handler would otherwise navigate away mid-read.
   -------------------------------------------------------------------------- */
(function () {
  var popover = null;
  var termEl = null;
  var defEl = null;
  var openTrigger = null;

  function buildPopover() {
    if (popover) return popover;
    popover = document.createElement("div");
    popover.className = "spec-glossary-popover";
    popover.setAttribute("role", "dialog");
    popover.setAttribute("aria-live", "polite");
    popover.hidden = true;
    termEl = document.createElement("p");
    termEl.className = "spec-glossary-popover__term";
    defEl = document.createElement("p");
    defEl.className = "spec-glossary-popover__def";
    popover.appendChild(termEl);
    popover.appendChild(defEl);
    document.body.appendChild(popover);
    return popover;
  }

  function closePopover() {
    if (!popover || popover.hidden) return;
    popover.hidden = true;
    if (openTrigger) {
      openTrigger.setAttribute("aria-expanded", "false");
      openTrigger = null;
    }
  }

  function openPopover(trigger) {
    var pop = buildPopover();
    termEl.textContent = trigger.getAttribute("data-spec-term") || trigger.textContent;
    defEl.textContent = trigger.getAttribute("data-spec-def") || "";
    pop.hidden = false;
    openTrigger = trigger;
    trigger.setAttribute("aria-expanded", "true");

    /* Measure after unhiding so offsetWidth/Height are real, then clamp to
       the viewport; flip above the trigger when there is no room below. */
    var rect = trigger.getBoundingClientRect();
    var margin = 12;
    var top = rect.bottom + 8;
    var left = rect.left;
    if (left + pop.offsetWidth > window.innerWidth - margin) {
      left = window.innerWidth - margin - pop.offsetWidth;
    }
    if (left < margin) left = margin;
    if (top + pop.offsetHeight > window.innerHeight - margin) {
      top = rect.top - pop.offsetHeight - 8;
    }
    if (top < margin) top = margin;
    pop.style.top = top + "px";
    pop.style.left = left + "px";
  }

  document.addEventListener("click", function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    var trigger = target.closest(".spec-glossary-trigger");
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      if (openTrigger === trigger) {
        closePopover();
      } else {
        closePopover();
        openPopover(trigger);
      }
      return;
    }

    if (popover && !popover.hidden && !target.closest(".spec-glossary-popover")) {
      closePopover();
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") closePopover();
  });

  /* A fixed-position popover drifts away from its term the moment anything
     scrolls, so any scroll simply closes it. Capture phase catches scrolls
     inside inner scrollers (drawers, the swatch row) that never bubble. */
  document.addEventListener("scroll", closePopover, true);
  window.addEventListener("resize", closePopover);
})();

})();
